import useWidth from '@components/Hooks/useWidth';
import Icon from '@components/v2/ui/Icon/Icon';
import ListItem from '@components/v2/ui/List/ListItem';
import ListItemButton from '@components/v2/ui/List/ListItemButton';
import ListItemText from '@components/v2/ui/List/ListItemText';
import Modal from '@components/v2/ui/Modal/Modal';
import MoreActionsButton from '@components/v2/ui/MoreActionsButton/MoreActionsButton';
import { MoreActionsItem } from '@components/v2/ui/MoreActionsButton/moreActionsButton.types';
import { Box, InputBase } from '@mui/material';
import { Pen, Trash } from 'lucide-react';
import { FocusEvent } from 'react';
import LangEnum from '../../../../../../lang/LangEnum';
import { RachelChatOutput } from '../../../../../../services/modules/rachelChats/rachelChats.types';
import { useTranslation } from '../../../../../translation/TranslationContext';
import { useRachelChatHistoryItem } from './useRachelChatHistoryItem';

interface RachelChatHistoryItemProps {
  chat: RachelChatOutput;
  isActive: boolean;
  onSelect: () => void;
}

const RenameActionIcon = () => <Icon icon={Pen} size="md" />;
const DeleteActionIcon = () => <Icon icon={Trash} size="md" />;

const EDITING_TITLE_SLOT_PROPS = { primary: { component: 'div' } } as const;
const DISPLAY_TITLE_SLOT_PROPS = { primary: { noWrap: true } } as const;
const EDITING_ROW_STYLES = { width: '100%', backgroundColor: 'action.hover' };
const DISPLAY_ROW_STYLES = { width: '100%' };
const ACTIONS_SLOT_STYLES = { display: 'flex', alignItems: 'center', gap: 1, paddingRight: 0.5 };
const UNREAD_DOT_STYLES = {
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: 'custom.teceo.orange',
  flexShrink: 0,
  marginRight: 1,
} as const;

const RachelChatHistoryItem = ({ chat, isActive, onSelect }: RachelChatHistoryItemProps) => {
  const { translate } = useTranslation();
  const { mobileWidth } = useWidth();
  const {
    isConfirmOpen,
    isDeleting,
    isEditing,
    isSelected,
    isUnread,
    draftTitle,
    draftInputRef,
    handleDeleteClick,
    handleCancelDelete,
    handleConfirmDelete,
    handleRenameStart,
    handleRenameChange,
    handleRenameConfirm,
    handleRenameKeyDown,
  } = useRachelChatHistoryItem({ chatId: chat.id, title: chat.title, isActive });

  const actions: MoreActionsItem[] = [
    {
      label: LangEnum.RACHEL_RENAME_CHAT,
      icon: RenameActionIcon,
      onClick: handleRenameStart,
    },
    {
      label: LangEnum.GLOBAL_DELETE,
      icon: DeleteActionIcon,
      onClick: handleDeleteClick,
      variant: 'destructive',
    },
  ];

  return (
    <>
      <ListItem
        className="rachel-chat-history-item"
        sx={{
          '&:hover .rachel-chat-history-item-actions': { opacity: 1 },
          '&:has(:focus-visible) .rachel-chat-history-item-actions': { opacity: 1 },
          '&:has([aria-expanded="true"]) .rachel-chat-history-item-actions': { opacity: 1 },
          '& .rachel-chat-history-item-actions [aria-expanded="true"]': {
            backgroundColor: 'custom.border.secondary',
          },
        }}
        secondaryAction={
          isEditing ? undefined : (
            <Box sx={ACTIONS_SLOT_STYLES}>
              <Box
                className="rachel-chat-history-item-actions"
                sx={{ opacity: mobileWidth ? 1 : 0, transition: 'opacity 0.15s ease-in-out' }}
                onClick={(event) => event.stopPropagation()}
              >
                <MoreActionsButton actions={actions} testId="rachel-chat-history-item-actions" />
              </Box>
            </Box>
          )
        }
      >
        <ListItemButton
          testId="rachel-chat-history-item"
          isActive={isSelected}
          sx={isEditing ? EDITING_ROW_STYLES : DISPLAY_ROW_STYLES}
          onClick={isEditing ? undefined : onSelect}
        >
          {isUnread && <Box sx={UNREAD_DOT_STYLES} test-id="rachel-chat-history-item-unread" />}
          <ListItemText
            primary={
              isEditing ? (
                <InputBase
                  autoFocus
                  fullWidth
                  ref={draftInputRef}
                  value={draftTitle}
                  inputProps={{
                    'aria-label': translate(LangEnum.RACHEL_RENAME_CHAT),
                    onFocus: (event: FocusEvent<HTMLInputElement>) => event.currentTarget.select(),
                  }}
                  sx={{
                    font: 'inherit',
                    display: 'flex',
                    '& .MuiInputBase-input': { padding: 0, height: 'auto' },
                  }}
                  onChange={(event) => handleRenameChange(event.target.value)}
                  onBlur={handleRenameConfirm}
                  onKeyDown={handleRenameKeyDown}
                />
              ) : (
                (chat.title ?? translate(LangEnum.RACHEL_UNTITLED_CHAT))
              )
            }
            slotProps={isEditing ? EDITING_TITLE_SLOT_PROPS : DISPLAY_TITLE_SLOT_PROPS}
          />
        </ListItemButton>
      </ListItem>
      <Modal
        isOpen={isConfirmOpen}
        size="compact"
        testId="rachel-chat-history-delete-confirm"
        header={{ title: LangEnum.RACHEL_DELETE_CHAT_CONFIRM }}
        onClose={handleCancelDelete}
        footer={{
          primaryAction: {
            label: LangEnum.GLOBAL_DELETE,
            isLoading: isDeleting,
            onClick: handleConfirmDelete,
          },
          secondaryAction: { label: LangEnum.GLOBAL_CANCEL_LABEL, onClick: handleCancelDelete },
        }}
      />
    </>
  );
};

export default RachelChatHistoryItem;
